import { roundedClassname } from '@/helpers/environmentHelper';
import { useAppSelector } from '@/state/hooks';
import { selectNotifications } from '@/state/reducers/notificationSlice';
import useLanguage from '@/utils/hooks/useLanguage';
import { useScreenSizes } from '@/utils/providers/ScreenSizesProvider';
import cn from 'classnames';
import { Fragment, useMemo } from 'react';
import { MdOutlineNotifications } from 'react-icons/md';

interface NotificationButtonProps {
    size?: string;
    isActive: boolean;
    hasBorders?: boolean;
    onClick(): void;
}

const NotificationButton = ({ size = 'w-5 h-5', isActive, hasBorders = true, onClick }: NotificationButtonProps) => {
    const lang = useLanguage();
    const notifications = useAppSelector(selectNotifications);

    const { mobile, tablet, interim } = useScreenSizes();
    const isMobile = useMemo(() => mobile || tablet || interim, [mobile, tablet, interim]);

    const unseenCount = useMemo(() => {
        return notifications.filter((notification) => !notification.seen).length;
    }, [notifications]);

    const ping = useMemo(() => {
        const latest = notifications[0];
        if (latest) {
            switch (latest.type) {
                case 'info':
                    return {
                        dot: 'bg-sky-500',
                        wave: 'bg-sky-400'
                    };
                case 'success':
                    return {
                        dot: 'bg-brand-green',
                        wave: 'bg-brand-green-light'
                    };
                case 'error':
                    return {
                        dot: 'bg-brand-red',
                        wave: 'bg-brand-red-light'
                    };
                default:
                    return false;
            }
        }
        return false;
    }, [unseenCount, notifications[0]]);

    return (
        <button
            type="button"
            className={cn(
                'relative text-left text-sm flex gap-1 items-center text-brand-text transition duration-150 ease-in-out whitespace-nowrap lg:my-0 bg-brand-background-dark',
                roundedClassname,
                {
                    'border border-brand-neutral-700 p-1 rounded-md hover:border-brand-neutral-600': hasBorders,
                    'pl-1.5 pr-2.5': !isMobile && hasBorders && lang.locale !== 'ar',
                    'pl-2.5 pr-1.5': !isMobile && hasBorders && lang.locale === 'ar',
                    'py-2': !hasBorders,
                    'px-2 py-2 my-1': isMobile
                }
            )}
            onClick={onClick}>
            <div
                className={cn('relative flex', size, {
                    ['animate-wiggle']: ping && unseenCount > 0
                })}>
                {ping && unseenCount > 0 && (
                    <Fragment>
                        <span
                            className={cn(
                                'z-[2] absolute top-1 right-0 h-2.5 w-2.5 opacity-75 animate-ping',
                                {
                                    [ping.wave]: true
                                },
                                roundedClassname
                            )}
                        />
                        <span
                            className={cn(
                                'z-[2] absolute top-1 right-0 h-2.5 w-2.5 border-[1px] border-brand-background text-[8px]',
                                {
                                    [ping.dot]: true
                                },
                                roundedClassname
                            )}
                        />
                        {/* {unseenCount} */}
                        {/* {unseenCount > 0 && (
                            <span className="absolute top-0 right-0 bg-brand-red-light text-brand-module-title rounded-full w-5 h-5 flex items-center justify-center text-xs">
                                {unseenCount}
                            </span>
                        )} */}
                    </Fragment>
                )}
                <MdOutlineNotifications className={cn('absolute z-[1]', size)} />
            </div>
            {!isMobile && lang.settings.notificationsTab}
        </button>
    );
};

export default NotificationButton;
